import { create } from 'zustand'

export type Theme = 'light' | 'dark'
export type Density = 'compact' | 'comfortable'
export type Toast = { msg: string; tone: 'info' | 'success' | 'warning' | 'danger' } | null

type UIState = {
  theme: Theme
  density: Density
  navCollapsed: boolean
  cmdkOpen: boolean
  cmdkQuery: string
  toast: Toast
  toggleTheme: () => void
  toggleDensity: () => void
  setDensity: (d: Density) => void
  toggleNav: () => void
  setCmdk: (open: boolean) => void
  setCmdkQuery: (q: string) => void
  showToast: (msg: string, tone?: NonNullable<Toast>['tone']) => void
  dismissToast: () => void
}

let toastTimer: ReturnType<typeof setTimeout> | undefined
const LS = 'pc.ui'

function applyDoc(p: Partial<{ theme: Theme; density: Density }>) {
  const el = document.documentElement
  if (p.theme) el.setAttribute('data-theme', p.theme)
  if (p.density) el.setAttribute('data-density', p.density)
}

function load(): { theme: Theme; density: Density } {
  try {
    const raw = localStorage.getItem(LS)
    if (raw) return { theme: 'light', density: 'compact', ...JSON.parse(raw) }
  } catch {
    /* ignore */
  }
  return { theme: 'light', density: 'compact' }
}
const saved = load()
applyDoc(saved)

function persist(theme: Theme, density: Density) {
  try {
    localStorage.setItem(LS, JSON.stringify({ theme, density }))
  } catch {
    /* ignore */
  }
}

export const useUI = create<UIState>((set, get) => ({
  theme: saved.theme,
  density: saved.density,
  navCollapsed: false,
  cmdkOpen: false,
  cmdkQuery: '',
  toast: null,
  toggleTheme: () => {
    const theme = get().theme === 'light' ? 'dark' : 'light'
    applyDoc({ theme })
    persist(theme, get().density)
    set({ theme })
  },
  toggleDensity: () => {
    const density = get().density === 'compact' ? 'comfortable' : 'compact'
    applyDoc({ density })
    persist(get().theme, density)
    set({ density })
  },
  setDensity: (density) => {
    applyDoc({ density })
    persist(get().theme, density)
    set({ density })
  },
  toggleNav: () => set((s) => ({ navCollapsed: !s.navCollapsed })),
  setCmdk: (cmdkOpen) => set({ cmdkOpen, cmdkQuery: cmdkOpen ? '' : get().cmdkQuery }),
  setCmdkQuery: (cmdkQuery) => set({ cmdkQuery }),
  showToast: (msg, tone = 'info') => {
    if (toastTimer) clearTimeout(toastTimer)
    set({ toast: { msg, tone } })
    toastTimer = setTimeout(() => set({ toast: null }), 3600)
  },
  dismissToast: () => {
    if (toastTimer) clearTimeout(toastTimer)
    set({ toast: null })
  },
}))
